/**
 * usePlaylists — **播放清單（心情／用途分類）**（0013；`decisions/033`；產品負責人 2026-09-24）
 *
 * 責任（app-local hook；硬規則 A：頁面唔准寫底層邏輯）：
 *   ① 一次攞同一部機嘅清單（**後端第一次呼叫會 lazy 建立 12 個系統分類**）＋每類首數
 *   ② 加歌／移出（**idempotent**：後端照實回 `added:false`，前端亦要照實講）
 *   ③ 自訂清單（建立／改名／刪除）—— ⚠️ **刪清單唔會刪歌**（後端照實回 `songsKept`）
 *   ④ 一首歌入咗邊幾個清單（彈窗「已加入」著燈）
 *
 * ⚠️ 錯誤**照實**回（碼／原文），由頁面決定點講（唔准靜靜）—— 同 `useLibrary()` 一致。
 * ⚠️ **唔准自動幫用戶分類**（唔准用 AI `mood_tags` 猜）：分類係用戶自己嘅表達。
 */
import { useCallback, useEffect, useState } from 'react';
import { getDeviceId } from './useChatStream';
import type { LibrarySong } from './useLibrary';

export interface Playlist {
  id: string;
  /** 系統分類 slug（`src/playlists.ts`）／`custom:<uuid>` */
  slug: string;
  /** 自訂清單嘅名（系統分類 ＝ `null`；label 由 i18n 解） */
  name: string | null;
  system: boolean;
  count: number;
}

export interface PlaylistResult {
  ok: boolean;
  /** 後端原始錯誤碼（照實顯示；冇 ＝ 成功） */
  error?: string;
}

export function usePlaylists() {
  const [deviceId] = useState<string>(() => getDeviceId());
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch(`/api/playlist/list?deviceId=${encodeURIComponent(deviceId)}`);
      const data = (await res.json().catch(() => ({}))) as { playlists?: Playlist[]; error?: string };
      if (!res.ok) {
        setError(data.error ?? `http_${res.status}`);
        return false;
      }
      setPlaylists(data.playlists ?? []);
      setError(null);
      return true;
    } catch (err) {
      setError(String(err).slice(0, 120));
      return false;
    }
  }, [deviceId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await reload();
      if (alive) setHydrated(true);
    })();
    return () => {
      alive = false;
    };
  }, [reload]);

  /** 一次網絡動作（**唔准吞錯**：非 2xx → `{ok:false, error:<原始碼>}`） */
  const call = useCallback(
    async (
      path: string,
      init: { method: string; body?: Record<string, unknown> },
    ): Promise<{ ok: boolean; error?: string; data: Record<string, unknown> }> => {
      setBusy(true);
      try {
        /**
         * ⚠️ **`DELETE` 一律帶 `?deviceId=`**（唔靠 body：DELETE 帶 JSON body 唔係所有環境都穩）
         *   —— 後端兩條路都認（`deviceOfDelete()`），但前端用 query 最穩。
         */
        const url =
          init.method === 'DELETE'
            ? `${path}${path.includes('?') ? '&' : '?'}deviceId=${encodeURIComponent(deviceId)}`
            : path;
        const res = await fetch(url, {
          method: init.method,
          headers: { 'Content-Type': 'application/json' },
          body: init.body ? JSON.stringify({ deviceId, ...init.body }) : undefined,
        });
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        if (!res.ok) {
          return { ok: false, error: typeof data.error === 'string' ? data.error : `http_${res.status}`, data };
        }
        return { ok: true, data };
      } catch (err) {
        return { ok: false, error: String(err).slice(0, 120), data: {} };
      } finally {
        setBusy(false);
      }
    },
    [deviceId],
  );


  /** 加歌入清單（**已經喺裏面 → `data.added === false`**；前端要照實講） */
  const addSong = useCallback(
    async (playlistId: string, songId: string): Promise<PlaylistResult & { added?: boolean }> => {
      const r = await call(`/api/playlist/${encodeURIComponent(playlistId)}/songs`, {
        method: 'POST',
        body: { songId },
      });
      if (r.ok) {
        await reload();
        return { ok: true, added: r.data.added === true };
      }
      return { ok: false, error: r.error };
    },
    [call, reload],
  );

  /** 移出清單（⚠️ 唔會刪歌） */
  const removeSong = useCallback(
    async (playlistId: string, songId: string): Promise<PlaylistResult & { removed?: boolean }> => {
      const r = await call(
        `/api/playlist/${encodeURIComponent(playlistId)}/songs/${encodeURIComponent(songId)}`,
        { method: 'DELETE' },
      );
      if (r.ok) {
        await reload();
        return { ok: true, removed: r.data.removed === true };
      }
      return { ok: false, error: r.error };
    },
    [call, reload],
  );

  const createCustom = useCallback(
    async (name: string): Promise<PlaylistResult & { playlist?: Playlist }> => {
      const r = await call('/api/playlist/create', { method: 'POST', body: { name } });
      if (r.ok) {
        await reload();
        return { ok: true, playlist: (r.data.playlist as Playlist) ?? undefined };
      }
      return { ok: false, error: r.error };
    },
    [call, reload],
  );

  const renamePlaylist = useCallback(
    async (playlistId: string, name: string): Promise<PlaylistResult> => {
      const r = await call(`/api/playlist/${encodeURIComponent(playlistId)}`, { method: 'PATCH', body: { name } });
      if (r.ok) await reload();
      return r.ok ? { ok: true } : { ok: false, error: r.error };
    },
    [call, reload],
  );

  /** 刪清單（後端只准自訂清單；**歌會保留**） */
  const removePlaylist = useCallback(
    async (playlistId: string): Promise<PlaylistResult> => {
      const r = await call(`/api/playlist/${encodeURIComponent(playlistId)}`, { method: 'DELETE' });
      if (r.ok) await reload();
      return r.ok ? { ok: true } : { ok: false, error: r.error };
    },
    [call, reload],
  );

  /** 一首歌入咗邊幾個清單（`null` ＝ 讀唔到，唔准當「冇加入」） */
  const memberships = useCallback(
    async (songId: string): Promise<string[] | null> => {
      try {
        const res = await fetch(
          `/api/playlist/of-song/${encodeURIComponent(songId)}?deviceId=${encodeURIComponent(deviceId)}`,
        );
        if (!res.ok) return null;
        const data = (await res.json().catch(() => ({}))) as { playlistIds?: string[] };
        return data.playlistIds ?? [];
      } catch {
        return null;
      }
    },
    [deviceId],
  );

  /** 清單內嘅歌（**加入次序**；播放器／日記清單模式用） */
  const songsOf = useCallback(
    async (playlistId: string): Promise<{ songs: LibrarySong[]; error?: string }> => {
      try {
        const res = await fetch(
          `/api/playlist/${encodeURIComponent(playlistId)}/songs?deviceId=${encodeURIComponent(deviceId)}`,
        );
        const data = (await res.json().catch(() => ({}))) as { songs?: LibrarySong[]; error?: string };
        if (!res.ok) return { songs: [], error: data.error ?? `http_${res.status}` };
        return { songs: data.songs ?? [] };
      } catch (err) {
        return { songs: [], error: String(err).slice(0, 120) };
      }
    },
    [deviceId],
  );

  return {
    deviceId,
    playlists,
    hydrated,
    error,
    busy,
    reload,
    addSong,
    removeSong,
    createCustom,
    renamePlaylist,
    removePlaylist,
    memberships,
    songsOf,
  };
}
